import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

export default function AdminDashboard() {
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <p className="tracking-eyebrow text-[var(--eafg-muted)]" data-testid="admin-dashboard-loading">
        {t("common.loading")}
      </p>
    );
  }

  return (
    <div className="space-y-10" data-testid="admin-dashboard-page">
      <header>
        <p className="tracking-eyebrow text-[var(--eafg-muted)]">Panel general</p>
        <h1 className="font-serif-display text-5xl mt-2">{t("admin.dashboard")}</h1>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label={t("admin.visits")} value={data.visits.total} hint={`+${data.visits.last_7d} 7d`} testId="stat-visits" />
        <Stat label={t("admin.revenue")} value={`${data.orders.revenue.toFixed(2)} €`} testId="stat-revenue" />
        <Stat label={t("admin.ordersPaid")} value={`${data.orders.paid}/${data.orders.total}`} testId="stat-orders" />
        <Stat label={t("admin.productsCount")} value={data.catalog.products} testId="stat-products" />
        <Stat label={t("admin.usersCount")} value={data.catalog.users} testId="stat-users" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-[var(--eafg-line)] bg-white p-6">
          <h3 className="tracking-eyebrow text-[var(--eafg-muted)] mb-4">{t("admin.visitsLast7")}</h3>
          <div className="h-64 min-h-[16rem]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.visits.by_day}>
                <CartesianGrid stroke="#e7e5e4" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#57534e" fontSize={11} />
                <YAxis stroke="#57534e" fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#C45B3A" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-[var(--eafg-line)] bg-white p-6">
          <h3 className="tracking-eyebrow text-[var(--eafg-muted)] mb-4">{t("admin.mostViewed")}</h3>
          <ul className="space-y-3" data-testid="most-viewed-list">
            {data.most_viewed.map((p) => (
              <li key={p.id} className="flex items-center justify-between border-b border-[var(--eafg-line)] pb-3">
                <span className="font-serif-display text-lg">{p.name_es}</span>
                <span className="tabular-nums text-sm text-[var(--eafg-muted)]">{p.views || 0}</span>
              </li>
            ))}
            {data.most_viewed.length === 0 && (
              <li className="text-[var(--eafg-muted)] text-sm">—</li>
            )}
          </ul>
        </div>
      </div>

      {data.new_contacts > 0 && (
        <div className="border border-[var(--eafg-copper)] bg-[#fff1ec] p-4 text-[var(--eafg-copper-dark)]">
          {data.new_contacts} {data.new_contacts === 1 ? "mensaje nuevo" : "mensajes nuevos"} en contactos.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint, testId }) {
  return (
    <div className="border border-[var(--eafg-line)] bg-white p-5" data-testid={testId}>
      <p className="tracking-eyebrow text-[var(--eafg-muted)] text-[0.65rem]">{label}</p>
      <p className="font-serif-display text-3xl mt-2 tabular-nums">{value}</p>
      {hint && <p className="text-xs text-[var(--eafg-copper)] mt-1">{hint}</p>}
    </div>
  );
}
